import { useEffect, useState } from "react";
import { useAuth } from "contexts/AuthContext";
import { API_BASE } from "config/api";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";
import { useTheme } from "@mui/material/styles";

import StatTile from "shell/StatTile";
import MonthlyTrendChart from "shell/MonthlyTrendChart";
import CategoryDistributionChart from "shell/CategoryDistributionChart";
import WeeklyTitheWelfareChart from "shell/WeeklyTitheWelfareChart";

const money = (v) =>
  `GHS ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const sumSeries = (series) => series.reduce((s, d) => s + Number(d.amount || 0), 0);

/** % change between the last two points of a {month, amount} series. */
const monthOverMonthChange = (series) => {
  if (series.length < 2) return 0;
  const prev = Number(series[series.length - 2].amount || 0);
  const curr = Number(series[series.length - 1].amount || 0);
  if (prev === 0) return curr > 0 ? 100 : 0;
  return ((curr - prev) / prev) * 100;
};

const pct = (n) => `${n >= 0 ? "+" : ""}${n.toFixed(1)}% vs last month`;

/**
 * Treasurer dashboard — real data from AnalyticsController, chmeetings-style
 * tiles + Chart.js line/bar charts (replaces the old mock-data SVG charts).
 */
export default function TreasurerDashboard() {
  const theme = useTheme();
  const { church } = useAuth();

  const [offerings, setOfferings] = useState([]);
  const [tithes, setTithes] = useState([]);
  const [welfare, setWelfare] = useState([]);
  const [harvest, setHarvest] = useState([]);
  const [weeklyTitheWelfare, setWeeklyTitheWelfare] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!church?.id) return;
    const token = localStorage.getItem("authToken");
    const headers = { Accept: "application/json", Authorization: `Bearer ${token}` };

    Promise.all([
      fetch(`${API_BASE}/analytics/monthly-offerings?church_id=${church.id}`, { headers }).then(
        (r) => r.json()
      ),
      fetch(`${API_BASE}/analytics/monthly-tithes?church_id=${church.id}`, { headers }).then((r) =>
        r.json()
      ),
      fetch(`${API_BASE}/analytics/monthly-welfare?church_id=${church.id}`, { headers }).then((r) =>
        r.json()
      ),
      fetch(`${API_BASE}/analytics/monthly-harvest?church_id=${church.id}`, { headers }).then((r) =>
        r.json()
      ),
      fetch(`${API_BASE}/analytics/weekly-tithe-welfare?church_id=${church.id}`, { headers }).then(
        (r) => r.json()
      ),
    ])
      .then(([o, t, w, h, wk]) => {
        setOfferings(Array.isArray(o) ? o : []);
        setTithes(Array.isArray(t) ? t : []);
        setWelfare(Array.isArray(w) ? w : []);
        setHarvest(Array.isArray(h) ? h : []);
        setWeeklyTitheWelfare(Array.isArray(wk) ? wk : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [church?.id]);

  const offeringsTotal = sumSeries(offerings);
  const tithesTotal = sumSeries(tithes);
  const welfareTotal = sumSeries(welfare);
  const harvestTotal = sumSeries(harvest);
  const totalRevenue = offeringsTotal + tithesTotal + welfareTotal + harvestTotal;

  const combinedSeries = offerings.map((d, i) => ({
    amount:
      Number(d.amount || 0) +
      Number(tithes[i]?.amount || 0) +
      Number(welfare[i]?.amount || 0) +
      Number(harvest[i]?.amount || 0),
  }));
  const revenueChange = monthOverMonthChange(combinedSeries);

  if (loading) {
    return (
      <Box sx={{ py: 6 }}>
        <Typography>Loading treasurer dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      {/* Stat tiles */}
      <Grid container spacing={3} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatTile
            icon="payments"
            label="Total Offerings"
            value={money(offeringsTotal)}
            tone="primary"
            sub={pct(monthOverMonthChange(offerings))}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatTile
            icon="account_balance"
            label="Total Tithes"
            value={money(tithesTotal)}
            tone="success"
            sub={pct(monthOverMonthChange(tithes))}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatTile
            icon="volunteer_activism"
            label="Welfare Funds"
            value={money(welfareTotal)}
            tone="info"
            sub={pct(monthOverMonthChange(welfare))}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatTile
            icon="agriculture"
            label="Harvest Seeds"
            value={money(harvestTotal)}
            tone="warning"
            sub={pct(monthOverMonthChange(harvest))}
          />
        </Grid>
      </Grid>

      {/* Monthly trend charts */}
      <Grid container spacing={3} sx={{ mb: 1 }}>
        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Monthly Offerings Trend
            </Typography>
            <MonthlyTrendChart
              data={offerings}
              color={theme.palette.teal[500]}
              emptyLabel="No offerings recorded yet."
            />
          </Card>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Monthly Tithes Trend
            </Typography>
            <MonthlyTrendChart
              data={tithes}
              color={theme.palette.success.main}
              emptyLabel="No tithes recorded yet."
            />
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 1 }}>
        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Monthly Welfare Trend
            </Typography>
            <MonthlyTrendChart
              data={welfare}
              color={theme.palette.info.main}
              emptyLabel="No welfare payments recorded yet."
            />
          </Card>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Monthly Harvest Trend
            </Typography>
            <MonthlyTrendChart
              data={harvest}
              color={theme.palette.warning.main}
              emptyLabel="No harvest/seed payments recorded yet."
            />
          </Card>
        </Grid>
      </Grid>

      {/* Weekly trend + summary cards */}
      <Grid container spacing={3} sx={{ mb: 1 }}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Weekly Tithe &amp; Welfare (last 7 days)
            </Typography>
            <WeeklyTitheWelfareChart data={weeklyTitheWelfare} />
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card sx={{ p: 2.5 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Icon sx={{ color: "success.main" }}>trending_up</Icon>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Total Revenue
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={800} color="success.main" sx={{ mt: 1 }}>
                  {money(totalRevenue)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Combined from all sources, last 12 months
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ p: 2.5 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Icon sx={{ color: "primary.main" }}>insights</Icon>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Average Weekly
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={800} color="primary.main" sx={{ mt: 1 }}>
                  {money(totalRevenue / 52)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Based on the last 12 months
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ p: 2.5 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Icon sx={{ color: "info.main" }}>show_chart</Icon>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Growth Rate
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={800} color="info.main" sx={{ mt: 1 }}>
                  {revenueChange >= 0 ? "+" : ""}
                  {revenueChange.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This month vs last month
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Category distribution */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Category Distribution (last 12 months)
            </Typography>
            <CategoryDistributionChart
              labels={["Offerings", "Tithes", "Welfare", "Harvest"]}
              data={[offeringsTotal, tithesTotal, welfareTotal, harvestTotal]}
              colors={[
                theme.palette.teal[500],
                theme.palette.success.main,
                theme.palette.info.main,
                theme.palette.warning.main,
              ]}
            />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
